/* eslint complexity: ["error", {max: 11}] -- this overrides for the entire file */
import isNil from "lodash/isNil";

import { TextTestID } from "@/constants/TestID";
import {
  DocumentIcon,
  FAIconName,
  Icon,
  IconButton,
  IconButtonSize,
  Text,
} from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import {
  getDocumentTypeFromFileName,
  getFileExtension,
} from "@/utils/documents.util";
import { joinTestIDs } from "@/utils/string.util";

interface BaseDocumentCardProps extends ViewWithTestID {
  description?: string;
  errorLabel?: string;
  fileDisplayName?: string;
  fileName: string;
  id: string;
  isDismissible?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  isShareable?: boolean;
  onDismissPress?: () => void;
  onLongPress?: () => void;
  onPress?: (id: string) => void;
  onSharePress?: (id: string) => void;
}

interface DismissButtonProps {
  baseTestID: string;
  isDismissible: boolean;
  onDismissPress: () => void;
}
const DismissButton = ({
  baseTestID,
  isDismissible,
  onDismissPress,
}: DismissButtonProps) => {
  if (!isDismissible) {
    return null;
  }

  return (
    <Box
      onPress={onDismissPress}
      position="absolute"
      right={8}
      testID={joinTestIDs(baseTestID, "dismiss")}
      top={8}
    >
      <Icon
        color={ColorToken.NeutralPrimary}
        name={FAIconName.Times}
        size="sm"
        testID={joinTestIDs(baseTestID, "dismiss")}
      />
    </Box>
  );
};

const getDocumentDescription = ({
  baseDescription,
  errorLabel,
  isInvalid,
}: {
  baseDescription?: string;
  errorLabel?: string;
  isInvalid?: boolean;
}) => {
  if (isInvalid) {
    return errorLabel || "Document validation failed";
  }

  return baseDescription;
};

const BaseDocumentCard = ({
  description,
  errorLabel,
  fileDisplayName,
  fileName,
  id,
  isDismissible = false,
  isInvalid,
  isLoading,
  isShareable,
  onDismissPress,
  onLongPress,
  onPress,
  onSharePress,
  testID,
}: BaseDocumentCardProps) => {
  const baseTestID = joinTestIDs(testID, "document");

  const handlePress = () => {
    onPress?.(id);
  };

  const handleShare = () => {
    onSharePress?.(id);
  };

  const handleDismiss = () => {
    onDismissPress?.();
  };

  const documentType = getDocumentTypeFromFileName(fileName);
  const documentExtension = getFileExtension(fileName)?.toUpperCase();
  const documentDescription = description || documentExtension;
  const documentName = fileDisplayName || fileName;
  const isShareEnabled =
    isShareable && onSharePress && !isInvalid && !isLoading;

  return (
    <BaseCard
      isDisabled={isInvalid || isLoading || isNil(onPress)}
      onLongPress={onLongPress}
      onPress={handlePress}
      testID={baseTestID}
      variant="secondary"
    >
      <HStack alignItems="center" gap={12} minHeight={IconButtonSize}>
        <DocumentIcon
          documentType={documentType}
          isInvalid={isInvalid}
          isLoading={isLoading}
        />
        <VStack flex={1} gap={2} overflow="hidden">
          <Text
            numberOfLines={1}
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            variant="captionMedium"
          >
            {documentName}
          </Text>
          <Text
            color={ColorToken.NeutralSecondary}
            numberOfLines={1}
            testID={joinTestIDs(baseTestID, TextTestID.Description)}
            variant="caption"
          >
            {getDocumentDescription({
              baseDescription: documentDescription,
              errorLabel,
              isInvalid,
            })}
          </Text>
        </VStack>
        {isShareEnabled && (
          <IconButton
            accessibilityLabel={`Share ${documentName}`}
            iconName={FAIconName.ShareFromSquare}
            onPress={handleShare}
            testID={joinTestIDs(testID, "share")}
          />
        )}
      </HStack>
      <DismissButton
        baseTestID={baseTestID}
        isDismissible={isDismissible}
        onDismissPress={handleDismiss}
      />
    </BaseCard>
  );
};

export type { BaseDocumentCardProps };
export default BaseDocumentCard;
